import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CodeOutputTabs from './CodeOutputTabs';
import { useCourse } from '../../providers/CourseContext';
import Popup from './Popup';
import {
  useCompleteEnrollCourseMutation,
  useEnrollCourseMutation,
} from '../../redux/apiSlice';
import { useCodeAi } from '../../hooks/useCodeAi';
import { normalizeQuotes } from '../../utils/helpers';

const typingTabs = [
  { label: 'Output', id: 1, active: false },
  { label: 'My Code Dragon Tip', id: 2, active: true },
];

export default function TypingEditor() {
  const navigate = useNavigate();
  const [completeEnrollCourse] = useCompleteEnrollCourseMutation();
  const [enrollCourse] = useEnrollCourseMutation();

  const {
    courseId,
    language,
    allQuestions,
    currentQuestionIndex,
    isLastQuestion,
    nextQuestion,
    userInput,
    setUserInput,
    attemptTracking,
    incrementDebugHelp,
    submitAttempt,
    popupMessage,
    hidePopupMessage,
    showPopupMessage,
    clearUserInputFromSession,
  } = useCourse();

  const [currentSectionOrder, currentQuestionOrder] = useMemo(() => {
    if (!allQuestions) return [1, 1];
    const currentQuestion = allQuestions[currentQuestionIndex];
    const currentSectionOrder = currentQuestion?.section?.order || 1;
    const currentQuestionOrder = currentQuestion?.order_in_section || 1;
    return [currentSectionOrder, currentQuestionOrder];
  }, [allQuestions, currentQuestionIndex]);

  // Initialize the useCodeAi hook with language for typing course
  const {
    debugAiResponse,
    debugAiLoading,
    debugError,
    debugUserCode,
    resetCodeAi,
  } = useCodeAi({ language: language });

  const [isLoading, setIsLoading] = useState(false);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [debugOutput, setDebugOutput] = useState<string[]>([]);
  const [isError, setIsError] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(1);
  const [wpm, setWpm] = useState<number | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [isDebugUsed, setIsDebugUsed] = useState(false);
  const [isAiDebugLoading, setIsAiDebugLoading] = useState(false);
  const typingAreaRef = useRef<HTMLTextAreaElement>(null);
  const outputTabsRef = useRef<HTMLDivElement>(null);

  // Get current question data for content
  const currentQuestion = allQuestions?.[currentQuestionIndex ?? 0];

  // Get expected output from current question
  const expectedOutput = currentQuestion?.expected_output || '';

  // Prepare tab content
  const tipContent = currentQuestion?.tip || '';

  // Reset debug used state when question changes
  useEffect(() => {
    setIsDebugUsed(false);
    setActiveTabIndex(1);
  }, [currentQuestionIndex]);

  // Start timer when user begins typing
  useEffect(() => {
    const handleKeyDown = () => {
      if (startTime === null) {
        setStartTime(Date.now());
      }
    };

    const typingArea = typingAreaRef.current;
    if (typingArea) {
      typingArea.addEventListener('keydown', handleKeyDown);
      return () => {
        typingArea.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [startTime]);

  // Focus on typing area when component mounts
  useEffect(() => {
    if (typingAreaRef.current) {
      typingAreaRef.current.focus();
    }

    // Reset typing state when the question changes
    setWpm(null);
    setStartTime(null);
    setDebugOutput([]);
    setIsError(false);
  }, [currentQuestionIndex]);

  // Calculate words per minute
  const calculateWPM = () => {
    if (startTime === null) return null;

    const timeElapsed = (Date.now() - startTime) / 1000 / 60; // in minutes
    const words = userInput.trim().split(/\s+/).length;

    return Math.round(words / timeElapsed);
  };

  // Update debugOutput when AI response is received
  useEffect(() => {
    if (debugAiResponse) {
      // Create a new array with both WPM and AI feedback
      const currentWpm = wpm || 0;
      setDebugOutput([`Words per minute: ${currentWpm}`, '', debugAiResponse]);
      setIsAiDebugLoading(false);
    }
  }, [debugAiResponse]);

  // Show loading indicator while AI is processing
  useEffect(() => {
    if (debugAiLoading) {
      setIsAiDebugLoading(true);
    }
  }, [debugAiLoading]);

  // Handle Submit and Check - this counts as a submission attempt
  const handleSubmitAndCheck = async () => {
    setIsLoading(true);
    setStartTime(null);
    setDebugOutput([]);
    try {
      // Calculate WPM
      const currentWpm = calculateWPM();
      setWpm(currentWpm);

      // Check if input matches expected output
      console.log('userInput', userInput);
      console.log('expectedOutput', expectedOutput);
      const isCorrect =
        normalizeQuotes(userInput.trim()) ===
        normalizeQuotes(expectedOutput.trim());

      // Create debug output for UI feedback
      let outputLines: string[] = [];

      if (isCorrect) {
        outputLines = [
          'Perfect match! Great job!',
          `Words per minute: ${currentWpm || 0}`,
        ];
        setIsError(false);
      } else {
        // For non-exact matches, use AI to provide helpful feedback
        await debugUserCode(
          userInput, // Student's code (typed text)
          expectedOutput, // Correct code (expected output)
          '', // No specific prompt needed for typing
          '', // No expected output needed as it's in the correctCode param
        );

        // Basic comparison for immediate feedback while AI processes
        const userWords = userInput.trim().split(/\s+/);
        const expectedWords = expectedOutput.trim().split(/\s+/);

        outputLines = [
          'There are some differences between your input and the expected text.',
          `Words per minute: ${currentWpm || 0}`,
          '',
          'Getting AI feedback...',
        ];

        // Length differences
        if (userWords.length !== expectedWords.length) {
          outputLines.push(
            `- Word count mismatch: You typed ${userWords.length} words, but expected ${expectedWords.length} words.`,
          );
        }
        setUserInput('');
        setIsError(true);
      }

      setDebugOutput(outputLines);
      setActiveTabIndex(0);

      // Submit the attempt with result and WPM (This will increment attempt count)
      submitAttempt(isCorrect, currentWpm);

      // Reset debug used state after submission
      setIsDebugUsed(false);

      // Scroll to output tabs after a short delay to ensure content is rendered
      setTimeout(() => {
        if (outputTabsRef.current) {
          outputTabsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
    } catch (error) {
      console.error('Error during submission:', error);
      setDebugOutput([
        `Error checking your typing: ${(error as Error)?.message || 'Unknown error'}`,
        'Please try again.',
      ]);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to scroll to editors
  const scrollToEditors = () => {
    // Scroll to the top of the page where the editors are located
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleNextQuestion = async () => {
    setIsNextLoading(true);
    setDebugOutput([]);
    resetCodeAi();
    await nextQuestion();
    setIsNextLoading(false);
    setTimeout(() => {
      scrollToEditors();
    }, 100);
  };

  // Determine if Debug button should be disabled
  const isDebugButtonDisabled = () => {
    return isLoading || !userInput.trim() || isDebugUsed;
  };

  // Determine if Next button should be disabled
  const isNextButtonDisabled = () => {
    return (
      (isNextLoading ||
        !userInput.trim() ||
        attemptTracking.attemptCount === 0 ||
        !attemptTracking.isSubmitted) &&
      !attemptTracking.isCorrect
    );
  };

  // Add this check to determine if Submit and Check should be disabled
  const isSubmitDisabled =
    isLoading ||
    attemptTracking.isSubmitted ||
    !userInput.trim() ||
    attemptTracking.isCorrect;

  const updatedTabs = typingTabs.map((tab, index) => ({
    ...tab,
    active: index === activeTabIndex,
  }));

  const handleCompleteCourse = async () => {
    try {
      await completeEnrollCourse({
        course: courseId,
        is_completed: true,
      }).unwrap();
      clearUserInputFromSession();
      navigate(`/courses/completion/${courseId}`);
    } catch (error: any) {
      if (error?.data === 'Course Already Completed by the User') {
        navigate(`/courses/completion/${courseId}`);
      } else if (error?.data === 'User had not enrolled into the course yet.') {
        await enrollCourse({ course: courseId }).unwrap();
        handleCompleteCourse();
      } else {
        console.error('Error completing course:', error);
        showPopupMessage('Failed to complete course. Please try again.');
      }
    }
  };

  return (
    <div
      className="p-4 space-y-4"
      style={{
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
      }}
    >
      <div className="flex flex-col md:flex-row gap-3">
        <div className="max-w-screen-xl mx-auto flex-1 space-y-2 flex flex-col">
          <div
            className="p-4 border rounded-md bg-blue-100 text-gray-800"
            style={{
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
            }}
          >
            <p
              className="text-base md:text-lg"
              style={{
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
              }}
            >
              Type the text below exactly as it appears, including any symbols,
              punctuation, and capitalization.
            </p>
          </div>

          <div className="flex">
            <div
              className="p-4 border rounded-md bg-yellow-100 text-gray-800 flex-grow shadow-sm"
              style={{
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
              }}
            >
              <p
                className="text-base md:text-lg flex gap-4"
                style={{
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                }}
              >
                <span
                  className="font-bold text-lg md:text-xl"
                  style={{
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                  }}
                >
                  {currentSectionOrder}.{currentQuestionOrder}:
                </span>
                {'  '}
                {expectedOutput}
              </p>
            </div>
          </div>

          {/* Expected output display */}
          {/* <div className="flex">
            <div className="p-4 border rounded-md bg-gray-50 text-gray-800 flex-grow shadow-sm">
              <p className="text-sm font-semibold mb-1">Expected text:</p>
              <p className="text-base font-mono">{expectedOutput}</p>
            </div>
          </div> */}

          <div className="flex h-full">
            <div className="border rounded-md shadow-md overflow-x-auto flex-grow">
              <textarea
                ref={typingAreaRef}
                className="w-full h-40 p-3 border rounded-md font-mono"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onCopy={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                placeholder="Start typing here..."
                style={{
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                  MozUserSelect: 'text',
                  msUserSelect: 'text',
                }}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            </div>
          </div>

          {wpm !== null && (
            <div className="text-right text-sm font-medium">
              <span>Current typing speed: </span>
              <span className="text-blue-600 font-bold">{wpm} WPM</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap justify-center mt-4 space-x-2 md:space-x-4">
        {/* <button
          className={`mb-2 py-2 px-4 rounded-full ${
            isDebugButtonDisabled()
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-[#028848]'
          } text-white text-sm md:text-base`}
          onClick={() => handleDebug()}
          disabled={isDebugButtonDisabled()}
          title={
            isDebugUsed
              ? 'Debug already used. Submit your work first to debug again.'
              : ''
          }
        >
          {isLoading ? 'Checking...' : 'Debug with My Code Dragon'}
        </button> */}

        <button
          className="mb-2 py-2 px-4 rounded-full bg-[#028848] text-white text-sm md:text-base"
          onClick={() => setActiveTabIndex(1)}
        >
          My Code Dragon Tip
        </button>

        <button
          className={`mb-2 py-2 px-4 rounded-full ${
            isSubmitDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#023f81]'
          } text-white text-sm md:text-base`}
          onClick={handleSubmitAndCheck}
          disabled={isSubmitDisabled}
        >
          {isLoading
            ? 'Checking...'
            : attemptTracking.isSubmitted
              ? 'Submitted'
              : 'Submit and Check'}
        </button>

        <button
          className={`mb-2 px-4 md:px-6 py-2 rounded-full font-medium text-white text-sm md:text-base transition ${
            isNextButtonDisabled()
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
          onClick={handleNextQuestion}
          disabled={isNextButtonDisabled()}
          style={{ display: isLastQuestion ? 'none' : 'block' }}
        >
          {isNextLoading ? 'Loading...' : 'Next'}
        </button>

        {isLastQuestion && (
          <button
            className={`mb-2 px-4 md:px-6 py-2 rounded-full font-medium text-white text-sm md:text-base transition ${
              isNextButtonDisabled()
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
            disabled={isNextButtonDisabled()}
            onClick={handleCompleteCourse}
          >
            Complete Course
          </button>
        )}
      </div>

      <div ref={outputTabsRef}>
        <CodeOutputTabs
          isError={isError}
          output={debugOutput}
          language="typing"
          tabs={updatedTabs}
          activeTab={activeTabIndex}
          setActiveTab={setActiveTabIndex}
          tipContent={tipContent}
          debugAiResponse={debugAiResponse}
          debugAiLoading={debugAiLoading || isAiDebugLoading}
          debugError={debugError}
        />
      </div>

      {/* Popup for success/error messages */}
      <Popup
        isOpen={popupMessage.isOpen}
        message={popupMessage.message}
        onClose={hidePopupMessage}
        onConfirm={popupMessage.onConfirm}
        confirmText={popupMessage.confirmText}
      />
    </div>
  );
}
